import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FaGripVertical, FaTimes } from "react-icons/fa";
import "./TableCustomizer.css";

// ── The two-pane column picker ────────────────────────────────────────────────
//
// Left:  every column of this listing, with checkboxes and a select-all.
// Right: the chosen columns in display order, reorderable by drag and drop.
//
// The two panes are the same list viewed two ways, so ticking on the left appends
// on the right and removing on the right unticks on the left. All of it is local
// draft state until Save — closing the modal throws the draft away, which is what
// makes experimenting with a layout safe.
//
// ── Why native HTML5 drag and drop ───────────────────────────────────────────
//
// A list of at most a few dozen text rows, reordered by one person at a time —
// not enough to justify a drag-and-drop dependency.
//
// The trade-off to know about: dragging is the ONLY way to reorder. HTML5 DnD does
// not fire for touch, and there is no keyboard path, so reordering is unavailable
// on a phone or tablet (removing a column with the × still works everywhere). If
// that becomes a problem, the fix is per-row move buttons or a pointer-events
// implementation, not a tweak here.

// Column ids are human labels ("First Name"), and an HTML id containing spaces
// silently breaks the label's `for` association — the box still toggles when you
// hit it dead-on, but clicking the text does nothing. Sanitising here is what
// keeps the whole row clickable.
function domId(prefix, id) {
  return `${prefix}-${String(id).replace(/[^\w-]+/g, "_")}`;
}

// Moves the item at `from` to `to`, returning a new array.
function reorder(list, from, to) {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

// ── Edge auto-scroll ──────────────────────────────────────────────────────────
//
// The order list scrolls once there are more columns than fit, and a native HTML5
// drag does NOT scroll its container — so without this, the last of fifteen
// columns simply cannot be dragged to the first position: you reach the top of the
// visible rows and the list stays put.
//
// How close to an edge scrolling starts, and how fast it goes at the very edge.
// The speed ramps linearly across the band so a small nudge inches along and
// parking at the edge (or past it) moves at full tilt.
const SCROLL_EDGE_PX = 52;
const SCROLL_MAX_PX_PER_FRAME = 16;

// Pixels to scroll this frame: negative up, positive down, 0 outside both bands.
function scrollStepFor(rect, pointerY) {
  const fromTop = pointerY - rect.top;
  const fromBottom = rect.bottom - pointerY;

  if (fromTop < SCROLL_EDGE_PX) {
    // clamp at 0 so dragging PAST the top edge stays at full speed rather than
    // going negative and reversing.
    const intensity = 1 - Math.max(fromTop, 0) / SCROLL_EDGE_PX;
    return -SCROLL_MAX_PX_PER_FRAME * intensity;
  }
  if (fromBottom < SCROLL_EDGE_PX) {
    const intensity = 1 - Math.max(fromBottom, 0) / SCROLL_EDGE_PX;
    return SCROLL_MAX_PX_PER_FRAME * intensity;
  }
  return 0;
}

export default function CustomizeTableModal({
  show,
  onHide,
  // [{ id, label }] — every customisable column of this listing.
  options = [],
  // Ordered ids currently applied. The draft starts as a copy of this.
  selectedIds = [],
  isCustomized = false,
  saving = false,
  onSave,
  onReset,
  title = "Customize Table",
}) {
  const [draft, setDraft] = useState(selectedIds);
  const [search, setSearch] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Re-seed the draft every time the modal opens, so a cancelled edit doesn't
  // linger into the next open.
  useEffect(() => {
    if (show) {
      setDraft(selectedIds);
      setSearch("");
      setDragIndex(null);
      setDragOverIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const labelById = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(o.id, o.label));
    return map;
  }, [options]);

  const visibleOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, search]);

  const selectedSet = useMemo(() => new Set(draft), [draft]);
  const allSelected = options.length > 0 && draft.length === options.length;
  const someSelected = draft.length > 0 && !allSelected;

  // "Select all" needs the third, indeterminate state to say "some but not all",
  // and that is a DOM property with no React attribute — hence the ref. It has to
  // hang off <Form.Check.Input>, not <Form.Check>, because the latter forwards its
  // ref to the wrapping div and the property would land somewhere harmless and
  // invisible.
  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  // The scrolling element of the order pane. Both the drag auto-scroll and the
  // arrow buttons' scroll-into-view drive it.
  const orderListRef = useRef(null);

  // Ticking appends to the END of the order rather than slotting the column back
  // into its original position: a column you just added is the one you are about
  // to place, and finding it at the bottom is more predictable than hunting for
  // where it landed.
  const toggleColumn = (id) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setDraft((prev) => (prev.length === options.length ? [] : options.map((o) => o.id)));
  };

  const removeColumn = (id) => setDraft((prev) => prev.filter((x) => x !== id));

  // ── Edge auto-scroll while dragging ─────────────────────────────────────────
  //
  // Driven by requestAnimationFrame reading the last pointer position out of a
  // ref, rather than by scrolling inside the dragover handler. Browsers fire
  // dragover on a ~350ms cadence, so scrolling a fixed step per event would lurch
  // three times a second instead of gliding; the rAF loop keeps moving between
  // events, and holding still at the edge keeps scrolling because the last known
  // pointer position stays in the ref.
  const pointerYRef = useRef(null);
  const rafRef = useRef(null);

  const stopAutoScroll = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pointerYRef.current = null;
  };

  const startAutoScroll = () => {
    if (rafRef.current != null) return;
    const tick = () => {
      const el = orderListRef.current;
      const pointerY = pointerYRef.current;
      if (el && pointerY != null) {
        const step = scrollStepFor(el.getBoundingClientRect(), pointerY);
        if (step !== 0) el.scrollTop += step;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  // A drag abandoned outside the window fires neither drop nor (reliably) dragend,
  // so the loop has to be torn down on unmount and on close as well.
  useEffect(() => stopAutoScroll, []);
  useEffect(() => { if (!show) stopAutoScroll(); }, [show]);

  // ── Drag handlers ───────────────────────────────────────────────────────────

  const handleDragStart = (index) => (event) => {
    setDragIndex(index);
    // Firefox ignores a drag that carries no data, and needs the effect set here
    // rather than only on dragover.
    event.dataTransfer.effectAllowed = "move";
    try { event.dataTransfer.setData("text/plain", String(index)); } catch (e) { /* IE-era guard */ }
  };

  const handleDragOver = (index) => (event) => {
    // Without preventDefault the browser refuses the drop outright.
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (index !== dragOverIndex) setDragOverIndex(index);
  };

  // On the scroll container, not the rows: this keeps feeding the auto-scroll loop
  // in the gaps between rows and in the padding above the first and below the
  // last, where no row's own handler fires.
  const handleListDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    pointerYRef.current = event.clientY;
    startAutoScroll();
  };

  const handleDrop = (index) => (event) => {
    event.preventDefault();
    // The container is a drop target too (it has to be, for auto-scroll). Stop
    // here so its handler doesn't also run and cancel this reorder.
    event.stopPropagation();
    // Fall back to the payload when state was lost (a drag that crossed frames).
    const from = dragIndex != null ? dragIndex : Number(event.dataTransfer.getData("text/plain"));
    if (!Number.isNaN(from)) setDraft((prev) => reorder(prev, from, index));
    setDragIndex(null);
    setDragOverIndex(null);
    stopAutoScroll();
  };

  // Dropping between rows or in the list's padding changes nothing. Mapping it to
  // "move to the end" would be a surprise for a 5px miss between two rows, and
  // preventDefault is still needed so the browser doesn't try to handle the drop
  // payload itself.
  const handleListDrop = (event) => {
    event.preventDefault();
    setDragIndex(null);
    setDragOverIndex(null);
    stopAutoScroll();
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
    stopAutoScroll();
  };

  const canSave = draft.length > 0 && !saving;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted small mb-3">
          Tick the columns you want to see, then drag them on the right to set their
          order. Your layout is saved to your account and applies only to you.
        </p>

        <div className="row g-3 tc-panes">
          {/* ── Available columns ───────────────────────────────────────────── */}
          <div className="col-12 col-md-6">
            <div className="tc-pane">
              <div className="tc-pane-header">
                <span>Columns</span>
                <span className="text-muted small">{draft.length} of {options.length}</span>
              </div>

              <div className="tc-pane-toolbar">
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search columns…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="tc-select-all">
                <Form.Check type="checkbox" id="tc-select-all">
                  <Form.Check.Input
                    type="checkbox"
                    ref={selectAllRef}
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                  <Form.Check.Label>
                    {allSelected ? "Deselect all" : "Select all"}
                  </Form.Check.Label>
                </Form.Check>
              </div>

              <div className="tc-list">
                {visibleOptions.length === 0 && (
                  <div className="tc-empty">No columns match “{search}”.</div>
                )}
                {visibleOptions.map((option) => (
                  <Form.Check
                    key={option.id}
                    type="checkbox"
                    id={domId("tc-col", option.id)}
                    className="tc-list-item"
                    label={option.label}
                    checked={selectedSet.has(option.id)}
                    onChange={() => toggleColumn(option.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ── Chosen columns, in order ────────────────────────────────────── */}
          <div className="col-12 col-md-6">
            <div className="tc-pane">
              <div className="tc-pane-header">
                <span>Display order</span>
                <span className="text-muted small">drag to reorder</span>
              </div>

              <div
                className="tc-list tc-order-list"
                ref={orderListRef}
                onDragOver={handleListDragOver}
                onDrop={handleListDrop}
              >
                {draft.length === 0 && (
                  <div className="tc-empty">
                    No columns selected. Pick at least one to save.
                  </div>
                )}

                {draft.map((id, index) => (
                  <div
                    key={id}
                    className={[
                      "tc-order-item",
                      dragIndex === index ? "tc-dragging" : "",
                      // The line marks where the row will actually land, and that
                      // depends on direction: reorder() removes before inserting,
                      // so dragging DOWN onto row N puts you after it, dragging UP
                      // puts you before it. One class for both would lie half the
                      // time.
                      dragOverIndex === index && dragIndex != null && dragIndex !== index
                        ? (dragIndex < index ? "tc-drop-below" : "tc-drop-above")
                        : "",
                    ].join(" ").trim()}
                    draggable
                    onDragStart={handleDragStart(index)}
                    onDragOver={handleDragOver(index)}
                    onDrop={handleDrop(index)}
                    onDragEnd={handleDragEnd}
                  >
                    <FaGripVertical className="tc-grip" aria-hidden="true" />
                    <span className="tc-order-index">{index + 1}</span>
                    <span className="tc-order-label">{labelById.get(id) || id}</span>

                    <span className="tc-order-actions">
                      <button
                        type="button"
                        className="tc-icon-btn tc-icon-btn-danger"
                        title="Remove column"
                        aria-label={`Hide ${labelById.get(id) || id}`}
                        onClick={() => removeColumn(id)}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div>
          {/* Only offered once there is something stored to clear. */}
          {isCustomized && (
            <Button variant="link" className="text-danger px-0" disabled={saving} onClick={onReset}>
              Reset to default
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="admin-btn"
            disabled={!canSave}
            title={draft.length === 0 ? "Select at least one column" : undefined}
            onClick={() => onSave(draft)}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
